import type { VaultItem } from './index';

/**
 * How the safe list is ordered. `updated-*` is by last-changed time,
 * `name-*` is alphabetical.
 *
 * Note the arrow convention the UI uses: ↓ is descending (newest first,
 * Z–A) and ↑ is ascending (oldest first, A–Z).
 */
export type SafeListSortOrder =
  | 'updated-desc'   // newest changed first — the historical behaviour
  | 'updated-asc'    // oldest changed first
  | 'name-asc'       // A–Z
  | 'name-desc';     // Z–A

export const DEFAULT_SAFE_LIST_SORT: SafeListSortOrder = 'updated-desc';

/** Every order, in the sequence the picker offers them. */
export const SAFE_LIST_SORT_ORDERS: readonly SafeListSortOrder[] = [
  'updated-desc', 'updated-asc', 'name-asc', 'name-desc',
];

/**
 * Returns a new array sorted for safe-list display.
 *
 * **Sensitive items always form a second tier at the bottom**, whatever the
 * order — the chosen sort applies *within* each tier. That is deliberate and
 * independent of ordering: an item marked sensitive should not be pulled to
 * the top of the list just because it was edited recently or named "AAA".
 *
 * Stable — preserves input order for items that compare equal. Pure: does not
 * mutate the input.
 *
 * The order argument defaults to `updated-desc`, which is exactly what this
 * function did before orders existed, so single-argument callers (the browser
 * extension) are unaffected.
 */
export function sortItemsForSafeList(
  items: readonly VaultItem[],
  order: SafeListSortOrder = DEFAULT_SAFE_LIST_SORT,
): VaultItem[] {
  const indexed = items.map((item, idx) => ({ item, idx }));

  // ISO 8601 strings compare lexicographically the same as their underlying
  // timestamps for the formats we produce, so string compare avoids a
  // `new Date()` per call. Items missing `updatedAt` (legacy / partial data)
  // sort to the end of their tier under `updated-desc`.
  const byUpdated = (a: VaultItem, b: VaultItem): number => {
    const aT = a.updatedAt ?? '';
    const bT = b.updatedAt ?? '';
    if (aT === bT) return 0;
    return aT < bT ? 1 : -1;            // descending
  };

  // localeCompare with sensitivity:'base' so "apple" sorts next to "Apple"
  // rather than after every capitalised name — a plain `<` puts all capitals
  // first, which reads as random ordering to anyone whose items are
  // inconsistently capitalised.
  const byName = (a: VaultItem, b: VaultItem): number =>
    (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });

  const base = order.startsWith('name') ? byName : byUpdated;
  // `updated-desc` and `name-asc` are the natural direction of their
  // comparator; the other two invert it.
  const inverted = order === 'updated-asc' || order === 'name-desc';

  const cmp = (a: typeof indexed[number], b: typeof indexed[number]): number => {
    const r = base(a.item, b.item);
    if (r !== 0) return inverted ? -r : r;
    return a.idx - b.idx;               // stable
  };

  const nonSensitive = indexed.filter((x) => !x.item.sensitive).sort(cmp);
  const sensitive    = indexed.filter((x) =>  x.item.sensitive).sort(cmp);
  return [...nonSensitive, ...sensitive].map((x) => x.item);
}
